import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink } from "react-router";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { applyBadge } from "@/lib/badge";
import { queryKeys } from "@/lib/queryClient";
import { cn } from "@/lib/utils";

/**
 * M23b — the badge.
 *
 * Its own endpoint rather than a slice of the list: this is mounted in the app shell on every
 * screen, and making it fetch a page of message bodies to learn one integer would put the
 * whole outbox on the wire on every navigation.
 *
 * It refetches on the live `notification` frame (see `useLiveEvents`), so the count moves the
 * moment something is recorded rather than on the next poll. The 60s interval is the backstop
 * for a dropped socket, not the mechanism.
 */
export function NotificationBell() {
  const query = useQuery({
    queryKey: queryKeys.unreadNotifications,
    queryFn: api.unreadNotifications,
    refetchInterval: 60_000,
  });

  const unread = query.data?.unreadCount ?? 0;

  // M34 — mirror the count onto the HOME-SCREEN icon. This effect is what makes reading a
  // notification take the badge down; the push handler can only ever push it up. Runs only
  // once the count has actually loaded: stamping the ?? 0 fallback would clear a real badge
  // during every cold start's first fetch.
  useEffect(() => {
    if (query.data) applyBadge(navigator, query.data.unreadCount);
  }, [query.data]);

  return (
    <NavLink
      to="/notifications"
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
      className={({ isActive }) =>
        cn(
          "relative inline-flex h-9 w-9 items-center justify-center rounded-md",
          isActive ? "text-primary" : "text-muted-foreground",
        )
      }
    >
      <Bell className="h-4 w-4" />
      {unread > 0 && (
        // Capped at 99+: three digits do not fit, and past ninety-nine the exact number has
        // stopped being the point.
        <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-destructive px-1 text-[10px] font-medium leading-4 text-destructive-foreground">
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </NavLink>
  );
}
